import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly stack: string | null;
}

/**
 * Phase 32 — Route-level error boundary.
 *
 * Bir alt ağaçtan fırlayan render-zamanı hatası tüm admin paneli düşürmesin
 * diye tüm route'lara sarılır. Operatöre Türkçe açıklama gösterilir; teknik
 * stack varsayılan olarak gizlidir, "Teknik detayları göster" disclosure
 * altında erişilebilir.
 */
export class RouteErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, stack: error.stack ?? null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (typeof console !== 'undefined') {
      console.error('[admin-web] route error boundary captured', { error, info });
    }
  }

  reset = (): void => {
    this.setState({ error: null, stack: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div>
          <div className="page-header">
            <div>
              <h1>Sayfa yüklenemedi</h1>
              <p>
                Bu bölümde beklenmeyen bir hata oluştu. Diğer sayfalara erişim
                etkilenmedi.
              </p>
            </div>
          </div>
          <div className="banner banner-err" role="alert">
            Sayfa açılırken bir sorun yaşandı. Tekrar denemek için aşağıdaki
            butonu kullan; sorun devam ederse teknik detayları açıp API
            log'ları ile karşılaştır.
          </div>
          <div className="card">
            <div className="action-row">
              <button onClick={this.reset}>Tekrar dene</button>
              <button
                className="ghost"
                onClick={() => {
                  window.location.href = '/';
                }}
              >
                Genel Bakış'a dön
              </button>
            </div>
            <details className="disclosure">
              <summary>Teknik detayları göster</summary>
              <div className="muted" style={{ marginTop: 8 }}>
                Aşağıdaki bilgi yalnızca operatör/destek için, müşteri yüzeyinde
                gösterilmez.
              </div>
              <pre style={{ marginTop: 8, fontSize: 11.5 }}>
                {this.state.error.name}: {this.state.error.message}
                {this.state.stack ? `\n\n${this.state.stack}` : ''}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
